/**
 * A long markdown document split into addressable pieces, and searched.
 *
 * Used for GUIDE.md and API.md. The guide is one long markdown file on purpose — it is edited as prose, not as React — but
 * rendered whole it was 650 lines on one screen with no way to find anything. Splitting happens
 * here, at read time, so the document never has to know it is paged: every `## ` is a section,
 * every `### ` inside one is a subsection, and each gets a stable slug for the URL.
 *
 * Pure functions over the markdown string, so they are tested without rendering anything.
 */

export interface GuideSubsection {
  slug: string;
  title: string;
  /** Markdown from this heading up to the next `### ` or the end of the section. */
  body: string;
}

export interface GuideSection {
  slug: string;
  title: string;
  /** The whole section, its own `## ` heading and every subsection included. */
  body: string;
  /** Text between the section heading and its first subsection — searched as its own unit. */
  lead: string;
  subsections: GuideSubsection[];
}

export interface ParsedGuide {
  intro: string;
  sections: GuideSection[];
}

/**
 * A heading as a URL fragment: "Resources → API Docs" becomes `resources-api-docs`.
 *
 * Parentheticals are dropped, so "Activity Log (admin only)" is `activity-log` — a qualifier
 * like that is the part most likely to be reworded, and a link should survive it.
 */
export const slugify = (title: string): string =>
  title
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[`*]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'section';

/** Makes each slug unique within `seen` by suffixing -2, -3… */
const uniqueSlug = (base: string, seen: Set<string>): string => {
  let slug = base;
  for (let n = 2; seen.has(slug); n++) slug = `${base}-${n}`;
  seen.add(slug);
  return slug;
};

/**
 * Split the guide at its headings.
 *
 * Fenced code is tracked so a `## ` inside an example is not mistaken for a heading. A `---`
 * at the end of a piece is dropped: it separated sections in one long scroll, and on a page of
 * its own it is a stray line.
 */
export const parseGuide = (markdown: string): ParsedGuide => {
  const lines = markdown.split('\n');
  const trimRule = (text: string) => text.replace(/\n---\s*$/, '').trim();

  const introLines: string[] = [];
  const sections: GuideSection[] = [];
  const sectionSlugs = new Set<string>();
  let current: { title: string; lines: string[] } | null = null;
  let inFence = false;

  const finish = () => {
    if (!current) return;
    const body = trimRule(current.lines.join('\n'));
    const slug = uniqueSlug(slugify(current.title), sectionSlugs);

    // Subsections, found the same way, fences respected
    const subsections: GuideSubsection[] = [];
    const subSlugs = new Set<string>();
    const leadLines: string[] = [];
    let sub: { title: string; lines: string[] } | null = null;
    let fence = false;
    for (const line of current.lines.slice(1)) {
      if (line.startsWith('```')) fence = !fence;
      const h3 = !fence && /^### (.+)$/.exec(line);
      if (h3) {
        if (sub) subsections.push({ title: sub.title, slug: uniqueSlug(slugify(sub.title), subSlugs), body: trimRule(sub.lines.join('\n')) });
        sub = { title: h3[1].trim(), lines: [line] };
      } else if (sub) {
        sub.lines.push(line);
      } else {
        leadLines.push(line);
      }
    }
    if (sub) subsections.push({ title: sub.title, slug: uniqueSlug(slugify(sub.title), subSlugs), body: trimRule(sub.lines.join('\n')) });

    sections.push({ slug, title: current.title, body, lead: trimRule(leadLines.join('\n')), subsections });
  };

  for (const line of lines) {
    if (line.startsWith('```')) inFence = !inFence;
    const h2 = !inFence && /^## (.+)$/.exec(line);
    if (h2) {
      finish();
      current = { title: h2[1].trim(), lines: [line] };
    } else if (current) {
      current.lines.push(line);
    } else {
      introLines.push(line);
    }
  }
  finish();

  return { intro: trimRule(introLines.join('\n')), sections };
};

/** Markdown reduced to the words a reader sees, for matching snippets against. */
export const plainText = (markdown: string): string =>
  markdown
    .replace(/^#{1,4}\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*|]/g, '')
    .replace(/^\s*[-:| ]{3,}\s*$/gm, '')
    .replace(/^\s*([-*]|\d+\.)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Regex-escape a search term. */
const escapeTerm = (term: string) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A pattern matching a term at the **start of a word**, so `rate` finds "rate limits" but not
 * "iterate" or "separately", while `limit` still finds "limited". Plain substring matching
 * made short words useless: half the results matched in the middle of unrelated words.
 * Shared with the page's highlighter so what is marked is exactly what matched.
 */
export const termPattern = (terms: string[], flags = 'i') =>
  new RegExp(`(?<![a-z0-9])(${terms.map(escapeTerm).join('|')})`, flags);

export const queryTerms = (query: string) => query.toLowerCase().split(/\s+/).filter(Boolean);

export interface GuideSearchResult {
  /** Empty for a match in the intro, which is shown on the overview. */
  sectionSlug: string;
  subsectionSlug?: string;
  /** "Calendar" or "Calendar › Moving a post to another day", formatting stripped */
  title: string;
  /** Plain text around the first match, with ellipses where it was cut. */
  snippet: string;
}

/**
 * Every place in the guide that mentions all of the words typed.
 *
 * Matched per subsection rather than per section, so a result lands on the paragraph that
 * answers the question instead of the top of a 200-line section. Every term must appear in
 * the same piece, in any order — the same rule as the What's Changed search — and headings
 * count, so typing a page name finds its section.
 */
export const searchGuide = (guide: ParsedGuide, query: string): GuideSearchResult[] => {
  const terms = queryTerms(query);
  if (!terms.length) return [];
  const each = terms.map(t => termPattern([t]));

  const scored: { result: GuideSearchResult; score: number; order: number }[] = [];
  const consider = (title: string, markdown: string, sectionSlug: string, subsectionSlug?: string) => {
    const text = plainText(markdown);
    title = title.replace(/[`*]/g, '');
    const haystack = `${title} ${text}`;
    if (!each.every(p => p.test(haystack))) return;

    // Ranked so the place *about* the words comes first: every term in the heading beats a
    // passing mention, then more mentions beat fewer. Document order breaks ties.
    const inTitle = each.every(p => p.test(title));
    const mentions = (text.match(termPattern(terms, 'gi')) || []).length;
    const score = (inTitle ? 1000 : 0) + mentions;

    // Centre the snippet on the first match in the body; a heading-only match shows the
    // opening words instead.
    const first = termPattern(terms).exec(text);
    const at = first ? first.index : 0;
    const start = Math.max(0, at - 70);
    const end = Math.min(text.length, start + 200);
    const snippet = `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
    scored.push({ result: { sectionSlug, subsectionSlug, title, snippet }, score, order: scored.length });
  };

  // The intro is content too — the API docs keep the base URL and how a request is put
  // together there — so it is searched, and a match opens the overview.
  if (guide.intro) consider('Overview', guide.intro.replace(/^# .*\n?/, ''), '');
  for (const section of guide.sections) {
    consider(section.title, section.lead, section.slug);
    for (const sub of section.subsections) {
      consider(`${section.title} › ${sub.title}`, sub.body.replace(/^### .*\n?/, ''), section.slug, sub.slug);
    }
  }
  return scored
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .map(s => s.result);
};
